import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook to manage visibility state based on user activity
 * @param {Object} options - Configuration options
 * @param {number} options.delay - Delay in ms before setting invisible to true
 * @param {any} options.deps - Dependencies to watch for changes (like props)
 * @param {boolean} options.visibleOnPropsChange - Reset visibility when deps change
 * @returns {boolean} invisible - Current invisible state
 */
const useInvisibler = ({ delay = 0, deps = [], visibleOnPropsChange = false } = {}) => {
	const [invisible, setInvisible] = useState(false);
	const timeoutRef = useRef(null);
	const mountedRef = useRef(false);

	const resetVisible = useCallback(() => {
		if (!mountedRef.current) {
			return;
		}

		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}

		if (delay && delay > 0) {
			setInvisible(false);
			timeoutRef.current = setTimeout(() => {
				setInvisible(true);
			}, delay);
		}
	}, [delay]);

	// Set up event listeners
	useEffect(() => {
		mountedRef.current = true;

		document.addEventListener("mousemove", resetVisible);
		document.addEventListener("keydown", resetVisible);
		window.addEventListener("focus", resetVisible);

		resetVisible();

		return () => {
			mountedRef.current = false;
			document.removeEventListener("mousemove", resetVisible);
			document.removeEventListener("keydown", resetVisible);
			window.removeEventListener("focus", resetVisible);
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, [resetVisible]);

	// Reset visibility when dependencies change
	useEffect(() => {
		if (visibleOnPropsChange) {
			resetVisible();
		}
	}, deps);

	return invisible;
};

export default useInvisibler;